import React, { useEffect, useRef, useState } from 'react';
import {
  AudioLines, BarChart3, BookOpen, Check, ChevronRight, Download, FilePen, FileSearch, FileSpreadsheet, FileText, Folder, Globe,
  Image as ImageIcon, KeyRound, LayoutGrid, Lock, Mic, Network, PanelLeft, Pencil, Presentation, Puzzle, RefreshCw, ScanEye, Server, Share2, ShieldCheck,
  Sparkles, Terminal, UserPlus, Users, Workflow, Wrench,
} from 'lucide-react';
import type { LandingCopy } from './content';

/** Adds `is-in` once an element scrolls into view (one short reveal per block). */
export function useInView<T extends HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return;
    if (!('IntersectionObserver' in window)) { setInView(true); return; }
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold });
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, threshold]);
  return { ref, inView };
}

export function Reveal({ as: Tag = 'div', className = '', children, ...rest }: { as?: 'div' | 'article' | 'ul'; className?: string; children: React.ReactNode } & React.HTMLAttributes<HTMLElement>) {
  const { ref, inView } = useInView<HTMLDivElement>(0.15);
  return React.createElement(Tag, { ref, className: `landing-reveal ${inView ? 'is-in' : ''} ${className}`, ...rest }, children);
}

/* Shared tiny pieces ------------------------------------------------ */

const FileBadge = ({ kind }: { kind: 'docx' | 'xlsx' | 'pptx' | 'hwp' | 'png' }) => {
  const Icon = kind === 'xlsx' ? FileSpreadsheet : kind === 'pptx' ? Presentation : kind === 'png' ? ImageIcon : FileText;
  return <span className={`mk-file is-${kind}`}><Icon size={14} /></span>;
};
const Prompt = ({ children }: { children: React.ReactNode }) => <div className="mk-prompt">{children}</div>;

/* Capability cards --------------------------------------------------- */

function ResearchMini({ copy }: { copy: LandingCopy }) {
  const m = copy.capabilities.mocks.research;
  return <div className="mk-card mk-answer"><div className="mk-chip"><b>3</b>{copy.ui.done}<ChevronRight size={12} /></div><h5>{m.heading}</h5>
    <table className="mk-table"><thead><tr>{m.head.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{m.rows.map(r => <tr key={r[0]}>{r.map((c, i) => <td key={i}>{c}</td>)}</tr>)}</tbody></table>
    <div className="mk-sources"><span><i /><i /><i /></span>{m.sources}</div></div>;
}
function DraftMini({ copy }: { copy: LandingCopy }) {
  const m = copy.capabilities.mocks.draft;
  return <div className="mk-card mk-page"><div className="mk-page-file"><FileBadge kind="docx" />{m.file}</div><h5>{m.title}</h5>
    <strong>{m.lines[0]}</strong><i className="mk-line" /><i className="mk-line is-short" /><strong>{m.lines[1]}</strong><i className="mk-line" />
    <p className="mk-added">{m.highlight}</p><i className="mk-line is-short" /></div>;
}
function ChartMini({ copy }: { copy: LandingCopy }) {
  const m = copy.capabilities.mocks.chart;
  const values = [52, 58, 55, 67, 74, 86];
  const points = values.map((v, i) => `${10 + i * 52},${110 - v}`).join(' ');
  return <div className="mk-card mk-chart"><h5>{m.title}</h5>
    <svg viewBox="0 0 280 120" aria-hidden="true"><g className="mk-grid">{[30, 60, 90].map(y => <line key={y} x1="0" x2="280" y1={y} y2={y} />)}</g>
      {values.map((v, i) => <rect key={i} x={i * 52} y={110 - v} width="20" height={v} rx="3" className={i === values.length - 1 ? 'is-hi' : ''} />)}
      <polyline points={points} /></svg>
    <div className="mk-months">{m.months.map(x => <span key={x}>{x}</span>)}</div>
    <div className="mk-output"><FileBadge kind="xlsx" /><span>{m.file}</span><Download size={13} /></div></div>;
}
function SlidesMini({ copy }: { copy: LandingCopy }) {
  const titles = copy.capabilities.mocks.slides;
  return <div className="mk-slides">{titles.map((title, i) => <div key={title} className={`mk-slide is-${i}`}>
    <span>{String(i + 1).padStart(2, '0')}</span><strong>{title}</strong>
    {i === 0 ? <em /> : i === 1 ? <div className="mk-slide-bars"><i /><i /><i /><i /></div> : i === 2 ? <div className="mk-slide-dots"><i /><i /><i /></div> : <div className="mk-slide-lines"><i /><i /><i /></div>}
  </div>)}</div>;
}
function HwpMini({ copy }: { copy: LandingCopy }) {
  const m = copy.capabilities.mocks.hwp;
  return <div className="mk-card mk-hwp"><div className="mk-page-file"><FileBadge kind="hwp" />HWPX</div><h5>{m.org}</h5><div className="mk-hwp-rule" />
    <p>{m.to}</p><p className="is-strong">{m.subject}</p><div className="mk-hwp-rule is-thin" /><p className="mk-hwp-body">{m.body}</p><i className="mk-line" /><i className="mk-line is-short" /></div>;
}
function ImageMini({ copy }: { copy: LandingCopy }) {
  return <div className="mk-card mk-image"><img src="/assets/image_sample_07.webp" alt="" loading="lazy" width="256" height="256" />
    <div className="mk-output"><FileBadge kind="png" /><span>{copy.capabilities.mocks.image}</span><Download size={13} /></div></div>;
}
const CAPABILITY_MOCKS = [ResearchMini, DraftMini, ChartMini, SlidesMini, HwpMini, ImageMini];

export function CapabilityCard({ copy, index }: { copy: LandingCopy; index: number }) {
  const card = copy.capabilities.cards[index];
  const Mock = CAPABILITY_MOCKS[index];
  return <Reveal as="article" className="landing-card" style={{ '--delay': `${(index % 3) * 70}ms` } as React.CSSProperties}>
    <div className={`landing-well is-tone-${index % 3}`} aria-hidden="true"><Prompt>{card.prompt}</Prompt><Mock copy={copy} /></div>
    <p className="landing-caption"><strong>{card.lead}</strong> {card.body}</p>
  </Reveal>;
}

const ROSTER_ICONS = [Globe, FileText, FileSpreadsheet, Presentation, FilePen, FileSearch, BarChart3, Terminal, ImageIcon, ScanEye, BookOpen, Puzzle];
export function AgentRoster({ copy }: { copy: LandingCopy }) {
  return <ul className="landing-roster">{copy.capabilities.roster.map(([name, desc], i) => {
    const Icon = ROSTER_ICONS[i];
    return <li key={name}><span className="landing-roster-icon"><Icon size={17} strokeWidth={1.8} /></span><span><strong>{name}</strong><small>{desc}</small></span></li>;
  })}</ul>;
}

/* Document agent: project wiki -------------------------------------- */

export function WikiMock({ copy }: { copy: LandingCopy }) {
  const w = copy.docs.wiki;
  const cite = <sup className="mk-cite">p.1</sup>;
  return <div className="mk-wiki" aria-hidden="true">
    <aside className="mk-wiki-tree">
      <div className="mk-segment"><span>{w.original}</span><span className="is-active">{w.wiki}</span></div>
      <div className="mk-tree-project"><ChevronRight size={13} className="is-open" /><Folder size={14} /><span>{w.project}</span><em><RefreshCw size={11} />{w.refresh}</em></div>
      {w.files.map((file, i) => <div key={file} className={`mk-tree-file ${i === 1 ? 'is-active' : ''}`}><FileText size={14} /><span>{file}</span></div>)}
      <div className="mk-tree-group"><ChevronRight size={13} className="is-open" /><Sparkles size={14} /><span>{w.keyPoints}</span><b>20</b></div>
      {w.concepts.map(concept => <div key={concept} className="mk-tree-concept"><BookOpen size={13} /><span>{concept}</span></div>)}
    </aside>
    <div className="mk-wiki-source">
      <div className="mk-wiki-bar"><span>{w.files[1]}</span></div>
      <div className="mk-wiki-canvas"><div className="mk-wiki-paper">
        <h6>{w.docTitle}</h6>
        {w.docSections.map(([heading, text], i) => <div key={heading} className={i === 1 ? 'is-linked' : ''}><strong>{heading}</strong><p>{text}</p></div>)}
      </div></div>
    </div>
    <article className="mk-wiki-page">
      <div className="mk-wiki-bar"><PanelLeft size={14} /><FileText size={14} /><strong>{w.files[1]}</strong><span className="mk-edit"><Pencil size={12} />{w.edit}</span></div>
      <div className="mk-wiki-body">
        <p>{w.summary}{cite}</p>
        <h6>{w.coreTitle}</h6>
        <ul>{w.core.map(([lead, text], i) => <li key={lead} className={i === 0 ? 'is-linked' : ''}><strong>{lead}</strong>: {text}{cite}</li>)}</ul>
      </div>
    </article>
  </div>;
}

/* Integrations -------------------------------------------------------- */

export const CONNECTORS = [
  { name: 'Slack', src: '/logo/slack.png' }, { name: 'Notion', src: '/logo/notion.png' }, { name: 'Atlassian', src: '/logo/atlassian.png' },
  { name: 'Gmail', src: '/logo/google_gmail.webp' }, { name: 'Google Calendar', src: '/logo/google_calendar.webp' }, { name: 'Google Drive', src: '/logo/google_drive.png' },
  { name: 'Microsoft Teams', src: '/logo/teams.webp' }, { name: 'Outlook', src: '/logo/outlook.webp' }, { name: 'OneDrive', src: '/logo/onedrive.png' },
  { name: 'GitHub', src: '/logo/github.png' }, { name: 'Discord', src: '/logo/discord.png' },
];

export function ConnectorWall() {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);
  const tiles = [...CONNECTORS.slice(0, 5), { name: 'askyour.ai', src: '/assets/agent_logo.webp' }, ...CONNECTORS.slice(5)];
  return <div ref={ref} className={`landing-wall ${inView ? 'is-in' : ''}`} role="img" aria-label={CONNECTORS.map(c => c.name).join(', ')}>
    <div className="landing-wall-grid">{tiles.map((tile, i) => <div key={tile.name} className={`landing-wall-tile ${tile.name === 'askyour.ai' ? 'is-brand' : ''}`} style={{ '--row': Math.floor(i / 6), '--col': i % 6 } as React.CSSProperties}>
      <span><img src={tile.src} alt="" loading="lazy" width="44" height="44" /></span>
    </div>)}</div>
  </div>;
}

export function FlowMock({ copy }: { copy: LandingCopy }) {
  const f = copy.integrations.flow;
  return <div className="mk-flow">
    <div className="mk-card mk-msg"><div className="mk-msg-head"><img src="/logo/slack.png" alt="" width="16" height="16" /><strong>{f.from}</strong></div><p>{f.message}</p></div>
    <div className="mk-flow-link"><img src="/assets/agent_logo.webp" alt="" width="22" height="22" /></div>
    <div className="mk-card mk-msg"><div className="mk-msg-head"><img src="/logo/atlassian.png" alt="" width="16" height="16" /><code>{f.to}</code><Check size={14} className="mk-ok" /></div><p>{f.result}</p></div>
  </div>;
}

export function ApprovalMock({ copy }: { copy: LandingCopy }) {
  const a = copy.integrations.approval;
  return <div className="mk-card mk-approval">
    <div className="mk-approval-label"><ShieldCheck size={13} />{a.label}</div><strong>{a.title}</strong>
    <span className="mk-approval-preview">{a.preview}</span>
    <dl>{a.fields.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
    <div className="mk-approval-actions"><span className="is-dark">{a.run}</span><span>{a.deny}</span></div>
  </div>;
}

export function ToggleMock({ copy }: { copy: LandingCopy }) {
  const t = copy.integrations.toggles;
  const logos: Record<string, string> = Object.fromEntries(CONNECTORS.map(c => [c.name, c.src]));
  return <div className="mk-card mk-toggles"><div className="mk-toggles-head"><Puzzle size={14} />{t.title}</div>
    {t.items.map(([name, on]) => <div key={name} className="mk-toggle-row"><img src={logos[name]} alt="" width="18" height="18" loading="lazy" /><span>{name}</span><span className={`mk-switch ${on ? 'is-on' : ''}`}><i /></span></div>)}
  </div>;
}

/* Case studies ----------------------------------------------------------- */

export function OnPremMock({ copy }: { copy: LandingCopy }) {
  const o = copy.cases.onprem;
  return <div className="mk-card mk-case-card">
    <div className="mk-case-head"><Server size={14} /><strong>{o.title}</strong><em>{o.badge}</em></div>
    {o.rows.map(([label, value]) => <div key={label} className="mk-onprem-row"><span>{label}</span><b>{value}</b><Check size={13} className="mk-ok" /></div>)}
    <div className="mk-onprem-row is-blocked"><span>{o.blocked[0]}</span><b><Lock size={11} strokeWidth={2.4} />{o.blocked[1]}</b></div>
  </div>;
}

/** Documents on the left feed concepts on the right; rows are 28px tall with a 10px gap, so link ends are fixed. */
const ONTOLOGY_LINKS: [number, number][] = [[0, 0], [0, 1], [1, 1], [1, 2], [2, 0], [2, 2]];
const ONTOLOGY_FILES = ['pdf', 'hwp', 'docx'] as const;
export function OntologyMock({ copy }: { copy: LandingCopy }) {
  const o = copy.cases.ontology;
  const y = (row: number) => 14 + row * 38;
  return <div className="mk-card mk-case-card">
    <div className="mk-case-head"><Network size={14} /><strong>{o.title}</strong></div>
    <div className="mk-graph">
      <ul>{o.docs.map((doc, i) => <li key={doc}><span className={`mk-file is-${ONTOLOGY_FILES[i]}`}><FileText size={12} /></span><span>{doc}</span></li>)}</ul>
      <svg viewBox="0 0 40 104" preserveAspectRatio="none">{ONTOLOGY_LINKS.map(([from, to]) => <path key={`${from}-${to}`} d={`M0 ${y(from)} C20 ${y(from)} 20 ${y(to)} 40 ${y(to)}`} />)}</svg>
      <ul className="is-concepts">{o.concepts.map(concept => <li key={concept}>{concept}</li>)}</ul>
    </div>
    <div className="mk-graph-foot"><img src="/assets/agent_logo.webp" alt="" width="14" height="14" />{o.agent}</div>
  </div>;
}

export function ErpMock({ copy }: { copy: LandingCopy }) {
  const e = copy.cases.erp;
  return <div className="mk-card mk-case-card">
    <div className="mk-case-head"><Workflow size={14} /><strong>{e.title}</strong></div>
    <ol className="mk-erp">
      <li><i /><div><span>{e.extract}</span><dl>{e.fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl></div></li>
      <li className="is-done"><i><Check size={9} strokeWidth={3} /></i><div><span>{e.done}</span><small>{e.voucher}</small></div></li>
    </ol>
  </div>;
}

/* Teams ----------------------------------------------------------------- */

export function TeamMock({ copy }: { copy: LandingCopy }) {
  const t = copy.teams.team;
  return <div className="mk-card mk-team">
    <div className="mk-team-head"><span className="mk-team-avatar">N</span><strong>{t.name}</strong><em>{t.role}</em></div>
    <div className="mk-team-stats"><div><Users size={14} /><b>12</b><small>{t.members}</small></div><div><KeyRound size={14} /><b>7.4M</b><small>{t.tokens}</small></div><div><Folder size={14} /><b>3.2 GB</b><small>{t.storage}</small></div></div>
    <div className="mk-team-bar"><span>{t.usage}</span><b>74%</b></div><div className="mk-progress"><i style={{ width: '74%' }} /></div>
    <div className="mk-team-foot"><span className="mk-avatars"><i>J</i><i>S</i><i>M</i><i>+9</i></span><small>{t.created}</small></div>
  </div>;
}

export function SkillsMock({ copy }: { copy: LandingCopy }) {
  const s = copy.teams.skills;
  return <div className="mk-card mk-skills">
    <div className="mk-tabs">{s.tabs.map((tab, i) => <span key={tab} className={i === 2 ? 'is-active' : ''}>{tab}</span>)}</div>
    {s.items.map(([name, meta]) => <div key={name} className="mk-skill"><span className="mk-skill-icon"><Wrench size={14} /></span><span><strong>{name}</strong><small>{meta}</small></span><Download size={14} /></div>)}
  </div>;
}

export function GroupChatMock({ copy }: { copy: LandingCopy }) {
  const c = copy.teams.chat;
  return <div className="mk-card mk-group">
    <div className="mk-group-head"><Users size={14} />{c.group}</div>
    {c.messages.map(([who, text], i) => <div key={i} className={`mk-group-msg ${who === 'askyour.ai' ? 'is-agent' : i === 2 ? 'is-me' : ''}`}>
      {who === 'askyour.ai' ? <img src="/assets/agent_logo.webp" alt="" width="22" height="22" /> : <span className="mk-avatar">{who.slice(0, 1)}</span>}
      <div><small>{who}</small><p>{text}</p>{who === 'askyour.ai' && <div className="mk-output"><FileBadge kind="docx" /><span>{copy.scenes[1].fileName}</span></div>}</div>
    </div>)}
  </div>;
}

export function DashboardMock({ copy }: { copy: LandingCopy }) {
  const d = copy.teams.dash;
  const cells = Array.from({ length: 7 * 18 }, (_, i) => {
    const v = (Math.sin(i * 1.7) + Math.cos(i * 0.9) + 2) / 4;
    return i > 7 * 11 ? Math.min(4, Math.round(v * 4 + 0.6)) : Math.round(v * 2.2);
  });
  return <div className="mk-card mk-dash">
    <div className="mk-dash-stats">{d.stats.map(([label, value]) => <div key={label}><small>{label}</small><b>{value}</b></div>)}</div>
    <div className="mk-dash-heat"><small>{d.activity}</small><div>{cells.map((v, i) => <i key={i} className={`l${v}`} />)}</div></div>
  </div>;
}

export function ControlsRow({ copy }: { copy: LandingCopy }) {
  const icons = [UserPlus, KeyRound, ShieldCheck, Share2];
  return <ul className="landing-controls">{copy.teams.controls.map(([lead, body], i) => {
    const Icon = icons[i];
    return <li key={lead}><Icon size={17} strokeWidth={1.8} /><p><strong>{lead}</strong> {body}</p></li>;
  })}</ul>;
}

/* Everyday tools -------------------------------------------------------- */

export function MemoryMock({ copy }: { copy: LandingCopy }) {
  const m = copy.everyday.memory;
  return <div className="mk-card mk-memory"><h6>{m.title}</h6>
    {m.items.map(([k, v]) => <div key={k} className="mk-memory-row"><small>{k}</small><p>{v}</p><span className="mk-evidence">{m.evidence}</span></div>)}
  </div>;
}

export function LiveMock({ copy }: { copy: LandingCopy }) {
  const l = copy.everyday.live;
  return <div className="mk-live">
    <div className="mk-orb"><AudioLines size={26} strokeWidth={1.6} /></div>
    <span className="mk-live-status"><i />{l.status}</span>
    <div className="mk-live-bubble is-user"><Mic size={12} />{l.user}</div>
    <div className="mk-live-bubble">{l.agent}</div>
  </div>;
}

export function TranslateMock({ copy }: { copy: LandingCopy }) {
  const t = copy.everyday.translate;
  return <div className="mk-card mk-translate">
    <div className="mk-translate-bar"><span className="mk-pills">{t.styles.map((s, i) => <em key={s} className={i === 2 ? 'is-active' : ''}>{s}</em>)}</span><span className="mk-glossary"><span className="mk-switch is-on"><i /></span>{t.glossary}</span></div>
    <div className="mk-translate-panes"><p>{t.source}</p><p className="is-target">{t.target}</p></div>
  </div>;
}

export function FeedMock({ copy }: { copy: LandingCopy }) {
  const f = copy.everyday.feed;
  return <div className="mk-feed"><small>{f.label}</small>{f.items.map(([tag, title], i) => <div key={title} className={`mk-card mk-feed-item is-${i}`}><em>{tag}</em><strong>{title}</strong><i className="mk-line" /><i className="mk-line is-short" /></div>)}</div>;
}

export const MORE_ICONS = [Folder, LayoutGrid, Wrench, Share2];
